import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, PLATFORM_ID, signal
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router, RouterLink } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

import { AuthConfig, LanguageService } from '@spartacus/core';

import { combineLatest, fromEvent, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { IAddressCard, SdAddressCardComponent } from '@seb/web-design-system/checkout';
import {
  BrandThemesEnum, SdBorderWrapperComponent, SdButtonComponent, SdFormErrorComponent, SdFormInputComponent,
  SdFormSelectComponent, SdListComponent, SdLoaderComponent, SdModalDefaultComponent, SdTitleComponent, StatusEnum
} from '@seb/web-design-system/core';

import { EditAddressFormComponent } from '@seb/web-common/core/components/static/edit-address-form/edit-address-form.component';
import { AuthenticationConfigInitializer } from '@seb/web-common/core/config/initializers/authentication-config.initializer';
import { CustomAddress, CustomAddressForm, CustomBaseStore, Profil } from '@seb/web-common/core/models';
import { GenderEnum } from '@seb/web-common/core/enums';
import {
  BaseStoreService, ConfigurationService, CustomAuthService, DynamicFormService, LoaderService, MyAccountService,
  ToasterService
} from '@seb/web-common/core/services';
import { IFrameResizerDirective } from '@seb/web-common/core/directives';

import '@seb/web-common/core/models/augmented-spartacus-core.model';

@Component({
  selector: 'wc-my-account-information',
  templateUrl: './my-account-information.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdTitleComponent,
    SdBorderWrapperComponent,
    FormsModule,
    ReactiveFormsModule,
    SdFormInputComponent,
    SdButtonComponent,
    SdFormErrorComponent,
    SdFormSelectComponent,
    RouterLink,
    SdListComponent,
    SdAddressCardComponent,
    SdLoaderComponent,
    SdModalDefaultComponent,
    EditAddressFormComponent,
    IFrameResizerDirective,
    TranslatePipe
  ]
})
export class MyAccountInformationComponent implements OnInit, OnDestroy {
  isEditAddress = signal(false);

  brandThemesEnum = BrandThemesEnum;
  isEditAccountModalOpened = false;
  isEditInfoModalOpened = false;
  isConfirmModalOpened = false;
  myAccountUrl: SafeResourceUrl;
  userForm: FormGroup;
  userInfoForm: FormGroup;
  infoButton: string;
  titleModal: string;
  addressesCard: IAddressCard[];
  addressEdited: CustomAddress = {};
  addressForm: CustomAddressForm = {};
  addressFormGroup: UntypedFormGroup;
  getAddresses: CustomAddress[];
  chosenAddressId: string;
  genders = [];
  selectedGender = '';
  urlContactUs = '';
  isLoading = true;
  isLoadingModal = false;
  isLoadingModalConfirm = false;
  private readonly destroy$: Subject<void> = new Subject();
  private baseStore: CustomBaseStore;

  constructor(
    private myAccountService: MyAccountService,
    private configService: ConfigurationService,
    private domSanitizer: DomSanitizer,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private baseStoreService: BaseStoreService,
    private languageService: LanguageService,
    private loaderService: LoaderService,
    private cdRef: ChangeDetectorRef,
    private authConfig: AuthConfig,
    private authService: CustomAuthService,
    private router: Router,
    private dynamicFrom: DynamicFormService,
    private toasterService: ToasterService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.baseStore = this.baseStoreService.baseStore;
    this.infoButton = this.translateService.instant('forms.user.actions.editInfo');
    this.titleModal = this.translateService.instant('forms.address.createTitle');
    this.addressFormGroup = this.formBuilder.group({
      addressGroup: this.formBuilder.group({})
    });
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.initIframeMessages();
    }

    this.initForm();

    combineLatest([
      this.myAccountService.getProfil(),
      this.myAccountService.getAddresses(),
      this.myAccountService.getGenres(),
      this.dynamicFrom.getAddressFormByCountry()
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([dataProfile, dataAddresses, titles, address]) => {
        this.addressForm = address;
        this.fillFormUserData(dataProfile);
        this.initAddresses(dataAddresses);
        this.genders = titles.map((title) => ({ value: GenderEnum[title.code], label: title.name }));
        this.urlContactUs = this.baseStore?.linkContanctUs ? new URL(this.baseStore.linkContanctUs).pathname : '';
        this.isLoading = false;
        this.cdRef.markForCheck();
      });
  }

  closeEditAddress(canceled: boolean): void {
    this.isEditInfoModalOpened = canceled;
  }

  savedAddress(address: CustomAddress): void {
    this.isLoadingModal = true;
    const saveAddress = address?.id
      ? this.myAccountService.editAddress(address)
      : this.myAccountService.addAddress(address);

    saveAddress.pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        this.initAddressSubscription();
        this.isEditInfoModalOpened = false;
        this.cdRef.markForCheck();
      },
      (error) => {
        this.isLoadingModal = false;
        if (error.status === 400) {
          this.toasterService.sendToasterEvent({
            status: StatusEnum.error,
            title: 'notify.notifyErrorToastTitle',
            description:
              error?.error?.errors[0]?.type === 'ValidationError'
                ? error.error.errors[0].message
                : 'notify.addressError'
          });
        }
        this.cdRef.markForCheck();
      }
    );
  }

  deletedAddress(id: string): void {
    this.isLoadingModal = true;
    this.myAccountService
      .deleteAddress(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => {
          this.initAddressSubscription();
          this.isEditInfoModalOpened = false;
          this.cdRef.markForCheck();
        },
        () => {
          this.isLoadingModal = false;
          this.cdRef.markForCheck();
        }
      );
  }

  getUrlRCU(): void {
    const config = this.configService.config;
    const language$ = this.languageService.getActive().pipe(take(1));
    language$
      .pipe(takeUntil(this.destroy$))
      .subscribe((language) => {
        const expId = AuthenticationConfigInitializer.getExpId(this.baseStore, config, language);
        this.myAccountUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(
          `${this.authConfig.authentication.baseUrl}/MyAccount?expid=${expId}`
        );
        this.cdRef.markForCheck();
      });
  }

  editAccount(): void {
    this.isEditAccountModalOpened = true;
    this.getUrlRCU();
  }

  editInfo(): void {
    this.userInfoForm.enable();
    const dataInfo = {
      firstName: this.userInfoForm.get('firstName').value,
      lastName: this.userInfoForm.get('lastName').value,
      birthdate: new Date(this.userInfoForm.get('birthdate').value),
      gender: this.userInfoForm.get('gender')?.value?.['value'],
      householdMembers: parseInt(this.userInfoForm.get('householdMembers').value, 10)
    };

    if (this.isEditAddress()) {
      if (this.userInfoForm.valid) {
        this.loaderService.showGlobalLoader();
        this.myAccountService
          .editProfil(dataInfo)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.userInfoForm.disable();
            this.isEditAddress.set(false);
            this.changeEditButton();
            this.loaderService.hideGlobalLoader();
            this.cdRef.markForCheck();
          });
      } else {
        this.userInfoForm.markAsPristine();
        this.isEditAddress.set(true);
      }
    } else {
      this.isEditAddress.set(true);
    }
    this.changeEditButton();
  }

  changeEditButton(): void {
    this.infoButton = this.isEditAddress()
      ? this.translateService.instant('forms.user.actions.saveInfo')
      : this.translateService.instant('forms.user.actions.editInfo');
  }

  addAddress(): void {
    this.addressEdited = {};
    this.isEditInfoModalOpened = true;
    this.titleModal = this.translateService.instant('forms.address.createTitle');
  }

  onAddressModalClose(isOpened: boolean): void {
    if (!isOpened) {
      this.addressEdited = {};
    }
  }

  editAddress(data: IAddressCard): void {
    this.isEditInfoModalOpened = true;
    this.getAddresses
      .filter((item) => item?.id === data?.id)
      .forEach((address) => {
        this.addressEdited = address;
      });
    this.titleModal = this.translateService.instant('forms.address.editTitle');
  }

  mainAddress(addressEdited: IAddressCard): void {
    this.chosenAddressId = addressEdited.id;
    this.isConfirmModalOpened = true;
  }

  cancelSwitch(): void {
    this.isConfirmModalOpened = false;
  }

  confirmMainAddress(): void {
    this.isLoadingModalConfirm = true;
    this.myAccountService
      .setMainAddress(this.chosenAddressId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isLoadingModalConfirm = false;
        this.isConfirmModalOpened = false;
        this.initAddressSubscription();
        this.cdRef.markForCheck();
      });
  }

  public initForm(): void {
    this.userForm = this.formBuilder.group({
      email: [{ value: '', disabled: true }],
      password: [{ value: '***********', disabled: true }]
    });
    this.userInfoForm = this.formBuilder.group({
      firstName: [{ value: '', disabled: true }, Validators.required],
      lastName: [{ value: '', disabled: true }, Validators.required],
      birthdate: [{ value: '', disabled: true }, Validators.required],
      householdMembers: [{ value: '', disabled: true }, Validators.required],
      gender: [{ value: '', disabled: true }]
    });
    this.userInfoForm.disable();
  }

  public initAddressSubscription(): void {
    this.myAccountService
      .getAddresses()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.initAddresses(data);
        this.isLoadingModal = false;
        this.cdRef.markForCheck();
      });
  }

  initIframeMessages(): void {
    fromEvent(window, 'message')
      .pipe(
        filter((messageEvent: MessageEvent) => messageEvent.origin !== window.origin),
        takeUntil(this.destroy$)
      )
      .subscribe((messageEvent: MessageEvent) => {
        if (messageEvent.data === 'salesforce_accountDeleted') {
          this.myAccountService.deleteProfile();
          this.authService.coreLogout();
          this.router.navigate(['']);
          this.cdRef.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private fillFormUserData(data: Profil) {
    if (Object.keys(data).length) {
      this.userForm?.patchValue({
        email: data?.email
      });
      this.userInfoForm.patchValue({
        firstName: data?.firstName,
        lastName: data?.lastName,
        birthdate: data?.birthdate,
        householdMembers: data?.householdMembers
      });
      if (data.gender) {
        this.selectedGender = data?.gender;
      }
    }
  }

  private initAddresses(addresses: CustomAddress []): void {
    this.getAddresses = addresses;
    this.addressesCard = addresses.map((item) => this.convertAddress(item));
  }

  private convertAddress(addressObject: CustomAddress): IAddressCard {
    return {
      firstname: addressObject.firstName ? addressObject.firstName : '',
      lastname: addressObject.lastName ? addressObject.lastName : '',
      addressName: addressObject.addressName ? addressObject.addressName : '',
      address: `${addressObject.streetNumber ? addressObject.streetNumber : ''} ${
        addressObject.streetName ? addressObject.streetName : ''
      }`,
      postalCode: addressObject.postalCode ? addressObject.postalCode : '',
      city: addressObject?.town,
      id: addressObject?.id,
      isMain: addressObject?.defaultAddress
    };
  }
}
